import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ChevronLeft, ChevronRight, PlayCircle } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";

export const VideoWall = ({ reviews }) => {
  const { t } = useLang();
  const s = t.testimonials;
  const vids = reviews.filter((r) => r.video_path);
  const [idx, setIdx] = useState(0);
  if (!vids.length) return null;
  const r = vids[idx % vids.length];
  const step = (d) => setIdx((i) => (i + d + vids.length) % vids.length);

  return (
    <div className="mb-16" data-testid="video-wall">
      <div className="flex items-center justify-between mb-5">
        <p className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#FBBC04]">
          <PlayCircle className="w-4 h-4" /> {s.videoWall}
        </p>
        {vids.length > 1 && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-white/35 mr-1" data-testid="video-wall-counter">
              {(idx % vids.length) + 1}/{vids.length}
            </span>
            <button
              onClick={() => step(-1)}
              data-testid="video-wall-prev"
              aria-label="Previous"
              className="w-9 h-9 rounded-full border border-white/15 hover:border-[#FF7900] hover:text-[#FF7900] flex items-center justify-center transition-colors duration-300"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => step(1)}
              data-testid="video-wall-next"
              aria-label="Next"
              className="w-9 h-9 rounded-full border border-white/15 hover:border-[#FF7900] hover:text-[#FF7900] flex items-center justify-center transition-colors duration-300"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
      <AnimatePresence mode="wait">
        <motion.div
          key={r.id}
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -30 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="border border-[#FBBC04]/25 bg-[#111111] p-5 md:p-7 flex flex-col md:flex-row gap-6 items-start"
          data-testid={`video-wall-card-${r.id}`}
        >
          <video
            controls
            preload="metadata"
            src={`${api.defaults.baseURL}/review-videos/${r.video_path}`}
            className="w-full md:w-[380px] max-h-64 rounded-sm border border-white/10 bg-black shrink-0"
            data-testid={`video-wall-player-${r.id}`}
          />
          <div className="flex-1">
            <div className="flex gap-1 mb-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} className={`w-4 h-4 ${i <= r.rating ? "text-[#FBBC04] fill-[#FBBC04]" : "text-white/20"}`} />
              ))}
            </div>
            <p className="text-sm md:text-base text-white/75 leading-relaxed mb-4">“{r.text}”</p>
            <p className="text-sm font-semibold text-[#FF7900]">— {r.name}</p>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};
